/**
 * Triggers a browser download for an in-memory `Blob` via a same-origin `blob:` object URL and a
 * temporary anchor click, then revokes the object URL.
 *
 * Extracted out of `downloadFile` (PHA-589) so a caller that already has the bytes in hand (e.g. an
 * authenticated file-download API response) can trigger the same save flow without needing a
 * fetchable `src` URL.
 */
export function saveBlobToFile(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  URL.revokeObjectURL(objectUrl);
}

/**
 * Triggers a browser download by navigating a temporary anchor to `url`, with **no** `fetch`.
 *
 * For signed storage URLs (e.g. the import file download endpoint, PHA-589) whose server response
 * pins `Content-Disposition: attachment`, that header alone is what makes the browser download the
 * resource instead of navigating to it — so a plain anchor click works with no CORS rule needed on
 * the storage account. Fetching the bytes first (as `downloadFile` does) would fail here since the
 * storage account has no CORS rule, and a `window.open` fallback issued after an awaited fetch
 * rejection runs outside the click's user-activation window, risking a popup block.
 *
 * The filename is pinned server-side via the `Content-Disposition` header, so this helper does not
 * (and, cross-origin, cannot) set the anchor's `download` attribute to control it.
 */
export function downloadFromUrl(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Downloads a file from `src` via `fetch` → `Blob` → object URL → temporary
 * anchor click, then revokes the object URL.
 *
 * A bare `<a download href={src}>` is silently ignored by browsers for
 * cross-origin URLs (e.g. blob storage) — clicking it just navigates the tab
 * to the raw file instead of downloading it. Fetching the bytes ourselves and
 * downloading from a same-origin `blob:` URL works regardless of the
 * resource's origin.
 *
 * Falls back to opening `src` in a new tab (`window.open`) when the fetch
 * itself fails (e.g. CORS denies the request) so the user can still access
 * the file, and the current tab/app is never lost to a failed navigation.
 */
export async function downloadFile(src: string, fileName?: string): Promise<void> {
  try {
    const response = await fetch(src);
    if (!response.ok) {
      throw new Error(`[downloadFile] Failed to fetch '${src}' (status ${response.status})`);
    }

    const blob = await response.blob();
    saveBlobToFile(blob, fileName ?? '');
  } catch {
    window.open(src, '_blank', 'noopener');
  }
}
